/**
 * Everything a feedback report carries besides the person's words and the
 * picture: where they were (path, app, tab, open ids), the device and
 * browser, the build, and the recent client errors.
 *
 * Deliberately NOT captured: form contents (nothing reads an input's value),
 * passwords, tokens, the authenticator code, the URL's query string or hash
 * (either can carry a token), storage, cookies. Who is sending is not sent
 * at all: the server takes it from the person's own Suite record.
 */
import { recentErrors } from "./errorBuffer.js";
import { currentPageIds } from "./pageIds.js";

/* global __SUITE_BUILD__ */
export function buildInfo() {
  try {
    const b = typeof __SUITE_BUILD__ !== "undefined" ? __SUITE_BUILD__ : null;
    if (b && typeof b === "object") {
      return { commit: b.commit || null, builtAt: b.builtAt || null, version: b.version || null };
    }
  } catch { /* not defined in this build */ }
  return { commit: null, builtAt: null, version: null };
}

export function deviceInfo(win = window) {
  const nav = win.navigator || {};
  let timezone = null;
  try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { /* old browser */ }
  return {
    viewportW: win.innerWidth ?? null,
    viewportH: win.innerHeight ?? null,
    screenW: win.screen?.width ?? null,
    screenH: win.screen?.height ?? null,
    pixelRatio: win.devicePixelRatio ?? null,
    userAgent: typeof nav.userAgent === "string" ? nav.userAgent.slice(0, 400) : null,
    platform: nav.userAgentData?.platform || nav.platform || null,
    language: nav.language || null,
    timezone,
    touch: typeof nav.maxTouchPoints === "number" ? nav.maxTouchPoints > 0 : null,
    online: typeof nav.onLine === "boolean" ? nav.onLine : null,
  };
}

/**
 * The report's context. `where` = { app, appName, tab } from the shell
 * (null app: the home page).
 */
export function buildFeedbackContext(where = {}, win = window) {
  return {
    page: {
      path: win.location?.pathname || "/",
      app: where.app || null,
      appName: where.appName || null,
      tab: where.tab || null,
      ids: currentPageIds(),
    },
    device: deviceInfo(win),
    build: buildInfo(),
    errors: recentErrors(),
    clientTime: new Date().toISOString(),
  };
}

/**
 * Whether "Include a picture of this page" starts ticked. Off where most
 * screens would copy money or people's personal details into the inbox:
 *   financials  money, behind its own passphrase (the inbox is not)
 *   ingest      guests: rosters, registrations, captures (their data is
 *               deleted on a schedule a picture would escape)
 *   crm         contacts' details and email bodies
 *   website     prospects' details and planner conversations, and the
 *               feedback inbox itself
 *   jobs        clients' and crew's names, emails and phones on job cards,
 *               quotes, saved conversations
 *   sheets      capture photographs
 * On elsewhere (the home page, Traffic Control, AutoCycle, Resources...).
 * The person can always tick it; then the parts that show guests are still
 * masked (data-feedback-private) and secrets and emails scrubbed
 * (screenshot.js).
 */
export const PICTURE_OFF_APPS = new Set(["financials", "ingest", "crm", "website", "jobs", "sheets"]);
export function pictureOnByDefault(appId) {
  return !PICTURE_OFF_APPS.has(appId || "");
}
